import { useLayoutEffect, useRef } from 'react';

/**
 * Urdu phonetic keyboard for fields where the user has no Urdu keyboard
 * installed: every key on an English keyboard types one fixed Urdu letter,
 * following the standard CRULP / Windows "Urdu Phonetic" layout (a → ا,
 * b → ب, Shift+T → ٹ, …). Unlike a Roman Urdu transliterator, nothing is
 * guessed or merged across keystrokes — what each key produces is always
 * the same, so a trained typist gets exactly the spelling they key in.
 *
 * Matches the Base and Shift faces of the official CRULP Urdu Phonetic
 * Keyboard Layout v1.1 chart (cle.org.pk, urdu_phonetic_kb_v1.1.pdf) key
 * for key, including keys that layout leaves empty (they type nothing).
 * The AltGr face (honorifics, rare diacritics) isn't supported. Spaces,
 * keys the layout leaves as-is (- = / \ ' " : _ + |) and anything already in
 * Urdu script (a native Urdu keyboard) pass through untouched.
 *
 * Mapped by the character a key produces on a US layout, so Shift+1 arrives
 * here as "!" and is mapped to the "1" the chart puts on that key.
 */
export const URDU_PHONETIC: Readonly<Record<string, string>> = {
  // Base: number row types Urdu digits
  '`': '',
  '1': '۱',
  '2': '۲',
  '3': '۳',
  '4': '۴',
  '5': '۵',
  '6': '۶',
  '7': '۷',
  '8': '۸',
  '9': '۹',
  '0': '۰',
  // Shift: number row types English digits
  '~': 'ً',
  '!': '1',
  '@': '2',
  '#': '3',
  $: '4',
  '%': '5',
  '^': '6',
  '&': '7',
  '*': '8',
  '(': '9',
  ')': '0',
  // Base: letters
  q: 'ق',
  w: 'و',
  e: 'ع',
  r: 'ر',
  t: 'ت',
  y: 'ے',
  u: 'ء',
  i: 'ی',
  o: 'ہ',
  p: 'پ',
  a: 'ا',
  s: 'س',
  d: 'د',
  f: 'ف',
  g: 'گ',
  h: 'ح',
  j: 'ج',
  k: 'ک',
  l: 'ل',
  z: 'ز',
  x: 'ش',
  c: 'چ',
  v: 'ط',
  b: 'ب',
  n: 'ن',
  m: 'م',
  // Shift: letters
  Q: 'ْ',
  W: 'ّ',
  E: 'ٰ',
  R: 'ڑ',
  T: 'ٹ',
  Y: 'َ',
  U: 'ئ',
  I: 'ِ',
  O: 'ۃ',
  P: 'ُ',
  A: 'آ',
  S: 'ص',
  D: 'ڈ',
  F: '',
  G: 'غ',
  H: 'ھ',
  J: 'ض',
  K: 'خ',
  L: '',
  Z: 'ذ',
  X: 'ژ',
  C: 'ث',
  V: 'ظ',
  B: '',
  N: 'ں',
  M: '٘',
  // Punctuation (brackets are swapped on the base face, as the chart has
  // them, so they look right in right-to-left text)
  '[': ']',
  ']': '[',
  ';': '؛',
  ',': '،',
  '.': '۔',
  '<': '',
  '>': '٫',
  '?': '؟',
};

/** Maps every key of `text` through the phonetic layout; unmapped characters pass through, empty keys type nothing. */
export function toUrduPhonetic(text: string): string {
  let out = '';
  for (const ch of text) out += URDU_PHONETIC[ch] ?? ch;
  return out;
}

type TextElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Drives a controlled text field as an Urdu phonetic keyboard. Whatever the
 * user types (physical keyboard or a phone's on-screen English keyboard) is
 * mapped key-for-key at the cursor, so editing mid-text works too. Pasted or
 * dropped text is kept as-is: it's already finished text, not keystrokes.
 *
 * Spread the returned `ref` and `onChange` onto the `<input>`/`<textarea>`.
 */
export function useUrduPhonetic<T extends TextElement>(value: string, onChange: (v: string) => void) {
  const ref = useRef<T>(null);
  // Where the caret belongs after React re-renders the mapped value; React
  // would otherwise leave it at the end whenever the value it renders differs
  // from what's in the DOM.
  const pendingCaret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const caret = pendingCaret.current;
    if (el && caret !== null && document.activeElement === el) {
      el.setSelectionRange(caret, caret);
    }
    pendingCaret.current = null;
  });

  function handleChange(e: { target: { value: string; selectionEnd?: number | null }; nativeEvent?: Event }) {
    const raw = e.target.value;
    const inputType = (e.nativeEvent as InputEvent | undefined)?.inputType ?? '';
    if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') {
      onChange(raw);
      return;
    }

    // Isolate what was inserted: it ends at the caret, and everything after
    // the caret is the old text's unchanged suffix. Without a usable caret,
    // fall back to the longest suffix the old and new text share.
    let rawEnd = raw.length;
    let oldEnd = value.length;
    const caret = e.target.selectionEnd;
    if (caret != null && value.endsWith(raw.slice(caret))) {
      rawEnd = caret;
      oldEnd = value.length - (raw.length - caret);
    } else {
      while (rawEnd > 0 && oldEnd > 0 && raw[rawEnd - 1] === value[oldEnd - 1]) {
        rawEnd--;
        oldEnd--;
      }
    }
    let start = 0;
    const maxPrefix = Math.min(rawEnd, oldEnd);
    while (start < maxPrefix && value[start] === raw[start]) start++;

    const inserted = raw.slice(start, rawEnd);
    const mapped = toUrduPhonetic(inserted);
    if (mapped === inserted) {
      onChange(raw);
      return;
    }
    pendingCaret.current = start + mapped.length;
    onChange(raw.slice(0, start) + mapped + raw.slice(rawEnd));
  }

  return { ref, onChange: handleChange };
}
